<!--
  Dashboard shell.

  Thin wrapper over `AppShell` from `@lucent/ui`, which owns the chrome both
  SPAs share: sticky header with brand lockup + nav + theme toggle, `<main>`,
  footer bar. What is left here is only what is specific to the dashboard — the
  organisation chip, the three nav sections and their active-state rules, the
  signed-in user chip, and the footer's live insights-api build + status link.

  The theme state machine moved with the chrome. `AppShell` installs
  `useTheme()` internally, so this file no longer touches `localStorage`,
  `prefers-color-scheme`, or the `data-theme` attribute.

  `main="bleed"` because page-level width treatment belongs to
  `InsightsPageBody` on each routed view — each page picks `is-full-bleed` or
  the centred-shell mode, so this file needs no allowlist of "which routes span
  the viewport".
-->

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";

import { AppShell } from "@lucent/ui";

import { getInsightsApiVersion, getInsightsApiStatusUrl } from "./api.ts";
import { canEdit, currentUser, logout } from "./auth.ts";

const route = useRoute();

// Display label for the signed-in user — prefers preferred_username,
// then email, then sub. Computed so it tracks the reactive ref.
const userLabel = computed(() =>
  currentUser.value
    ? currentUser.value.preferred_username ??
      currentUser.value.email ??
      currentUser.value.name ??
      currentUser.value.sub
    : null,
);

// Display label for the organisation chip in the top-left. Fallback chain:
// the registered friendly `client_name` → the `client_key` slug → the
// hard-coded "Dashboard" string. The slug fallback covers clients
// that haven't been given a `name` yet; the "Dashboard" string
// covers pre-auth render passes and the rare case where the
// login-time lookup against insights-api failed.
const organisationLabel = computed(
  () =>
    currentUser.value?.client_name ??
    currentUser.value?.client_key ??
    "Dashboard",
);

const onSignOut = (): void => {
  void logout();
};

// Highlight the "Insights" nav link for any of the dashboard's
// insight-related routes. The route names come from `router.ts`.
// `view` lights up Insights — opening an insight is a continuation
// of the catalogue, not the saved-views surface.
const insightsActive = computed(() =>
  ["list", "view", "versions", "diff"].includes(String(route.name ?? "")),
);

// "Views" nav highlight — the saved-views list page. Distinct from
// `insightsActive` because the two surfaces are siblings on the
// header strip.
const viewsActive = computed(() => route.name === "views");

// "Data Explorer" nav highlight — every route under `/data/...`. The
// router names sub-routes `data.sql` / `data.inspector` / etc.
const dataActive = computed(() =>
  String(route.name ?? "").startsWith("data."),
);

// The login screen is a full-bleed bespoke layout with its own brand
// chrome — suppress the global header / footer on that route so the
// LucentLake design can claim the whole viewport. This stays here rather
// than in AppShell: the shell reads no route state, and the app is the
// only party that knows which route is the bespoke one.
const isAuthRoute = computed(() => route.name === "login");

// insights-api version string, fetched once on shell mount from the
// public `/healthz` endpoint and rendered in the footer. `null` until
// the call resolves, and stays `null` on failure — the footer hides
// the slot in that case rather than showing a broken value.
const insightsApiVersion = ref<string | null>(null);

// Absolute URL of insights-api's `/status` page, backing the footer
// Status link. Resolved once on mount; `null` until it resolves and on
// failure, in which case the link falls back to an inert `#`.
const insightsApiStatusUrl = ref<string | null>(null);

onMounted(() => {
  // Surface the live insights-api build in the footer. Fire-and-forget;
  // the helper swallows errors and leaves the ref `null` on failure so
  // we don't need to handle the rejection here.
  void getInsightsApiVersion().then((v) => {
    insightsApiVersion.value = v;
  });

  // Resolve the Status link target the same way; fire-and-forget.
  void getInsightsApiStatusUrl().then((u) => {
    insightsApiStatusUrl.value = u;
  });
});
</script>

<template>
  <AppShell
    :chromeless="isAuthRoute"
    main="bleed"
    home-to="/"
    lockup-label="LucentLake home"
  >
    <template #brand-sub>organisation: {{ organisationLabel }}</template>

    <template #nav>
      <!-- Non-insights nav links stay commented out for the slimmed-down
           header: Home, Dashboards, Sources, Settings. -->
      <RouterLink to="/insights" :class="{ 'is-active': insightsActive }">Insights</RouterLink>
      <RouterLink to="/views" :class="{ 'is-active': viewsActive }">Views</RouterLink>
      <!--
        Editor-only. Hidden rather than disabled for the `user`
        tier: a visible-but-dead tab invites clicking, and the
        router guard would bounce it anyway.
      -->
      <RouterLink
        v-if="canEdit"
        to="/data"
        :class="{ 'is-active': dataActive }"
      >Data Explorer</RouterLink>
    </template>

    <!--
      Signed-in chrome: appears once `currentUser` is populated
      (after the session lookup resolves). The label prefers
      `preferred_username` so the IdP's human-friendly handle
      wins over the opaque `sub`.

      The Docs CTA that used to sit here is hidden alongside the rest of the
      non-insights chrome. The "Ask LucentLake" CTA beside it is gone with the
      /ai route — conversational authoring drives the MCP tool stack, which is
      editor-only, and the view was never more than a scaffold. Reinstate it
      with the route, not on its own.
    -->
    <template #header-actions>
      <span v-if="userLabel" class="lucent-user-chip" :title="userLabel">
        {{ userLabel }}
      </span>
      <button
        v-if="userLabel"
        type="button"
        class="lucent-btn lucent-btn--ghost"
        @click="onSignOut"
      >
        Sign out
      </button>
    </template>

    <RouterView />

    <template #footer>
      <a
        class="lucent-footer-status"
        :href="insightsApiStatusUrl ?? '#'"
        target="_blank"
        rel="noopener"
      >
        <span class="dot"></span>All systems normal
      </a>
      <span class="lucent-footer-sep"></span>
      <span>v1.1</span>
      <template v-if="insightsApiVersion">
        <span class="lucent-footer-sep"></span>
        <span :title="`insights-api ${insightsApiVersion}`">
          insights-api {{ insightsApiVersion }}
        </span>
      </template>
      <span class="lucent-footer-sep"></span>
      <span>© 2026 LucentLake ·
        <a
          href="https://www.savagebull.com.au"
          target="_blank"
          rel="noopener"
          >Savage Bull</a
        ></span>
    </template>
  </AppShell>
</template>
